import React, { useState, useMemo, useEffect } from 'react';
import { Table, Icon } from 'antd';
import cn from 'classnames';
import {
  TableFilterDropdown,
  TableFilterIcon,
} from '../../../../../../components/tableComponents';
import { FaHourglassStart } from 'react-icons/fa';
import PageSizeButton from '../../../../../../components/PageSizeButton/PageSizeButton';
import DspxPagination from '../../../../../../components/DspxPagination/DspxPagination';
import {
  columns,
  columnKeys,
  rowCounts,
  quickJumpSize,
  selectionCellWidth,
  components,
} from '../../config';
import {
  useCreateRowProps,
  useCreateRowSelection,
  useTableChange,
  sortAndFilterData as sortAndFilter,
} from '../../../../features/table';
import { createFilters } from '../../lib';
import ExpandedRow from '../ExpandedRow/ExpandedRow';
import styles from './ShutdownsTable.module.scss';
import { selectVisible } from '../../../../features/filterNew/model/selectors';
import { useSelector } from 'react-redux';

const { sortAndFilterData } = sortAndFilter;
const { Column } = Table;

const keyPathMap = {
  [columnKeys.reasonName]: 'alertReason.id',
  [columnKeys.typeName]: 'type.id',
};

const ShutdownsTable = ({ shutdowns, loading, className }) => {
  const [page, setPage] = useState(1);
  const [rowPerPage, setRowPerPage] = useState(rowCounts[0]);

  const visible = useSelector(selectVisible);

  const { sortedInfo, filteredInfo, handleTableChange } = useTableChange();

  const [prevFilteredInfo, setPrevFilteredInfo] = useState(filteredInfo);
  if (JSON.stringify(prevFilteredInfo) !== JSON.stringify(filteredInfo)) {
    setPage(1);
    setPrevFilteredInfo(filteredInfo);
  }

  const { expandedRowKeys, handleRow } = useCreateRowProps({
    colCount: columns.length,
    expandedRowRender: (record) => <ExpandedRow shutdown={record} />,
  });

  const sortedAndFilteredData = useMemo(
    () =>
      sortAndFilterData({
        items: shutdowns,
        sortedInfo,
        filteredInfo,
        keyPathMap,
      }),
    [shutdowns, sortedInfo, filteredInfo]
  );

  const filters = useMemo(
    () => createFilters(shutdowns, columnKeys),
    [shutdowns]
  );
  const start = (page - 1) * rowPerPage;
  const end = start + rowPerPage;

  const rowSelection = useCreateRowSelection({
    cellWidth: selectionCellWidth,
    data: sortedAndFilteredData,
    rowKey: 'alertId',
  });

  useEffect(() => {
    if (!visible) {
      setPage(1);
    }
  }, [visible]);

  return (
    <div className={cn(styles.root, className)}>
      <Table
        rowKey="alertId"
        onChange={handleTableChange}
        onRow={handleRow}
        pagination={false}
        components={components}
        rowSelection={rowSelection}
        dataSource={sortedAndFilteredData.slice(start, end)}
        expandIconAsCell={false}
        loading={{
          spinning: loading,
          indicator: (
            <Icon
              spin
              component={FaHourglassStart}
              style={{ fontSize: '24px', color: '#3378f9' }}
            />
          ),
        }}
      >
        {columns.map((column) => {
          const col =
            typeof column === 'function' ? column({ expandedRowKeys }) : column;

          const filter = filters[col.key];
          const hasFilter = filter?.size > 1;

          return (
            <Column
              title={col.title}
              dataIndex={col.dataIndex}
              key={col.key}
              colSpan={col.colSpan || 1}
              sorter={col.sorter || false}
              render={col.render}
              sortOrder={
                Boolean(col.getSortOrder) ? col.getSortOrder(sortedInfo) : false
              }
              // filters={hasFilter ? Array.from(filter.values()) : []}
              // filterIcon={
              //   hasFilter
              //     ? (filtered) => <TableFilterIcon filtered={filtered} />
              //     : false
              // }
              // filteredValue={filteredInfo?.[col.key] || null}
              // filterDropdown={
              //   hasFilter
              //     ? (props) => (
              //         <TableFilterDropdown
              //           {...props}
              //           filterMultiple={col.filterMultiple}
              //         />
              //       )
              //     : undefined
              // }
            />
          );
        })}
      </Table>
      <div className={styles.paginationWrap}>
        <PageSizeButton
          pageSize={rowPerPage}
          setPageSize={(rowPerPage) => {
            setRowPerPage(rowPerPage);
            setPage(1);
          }}
        />
        <DspxPagination
          current={page}
          total={sortedAndFilteredData.length}
          pageSize={rowPerPage}
          showQuickJumper={
            Math.ceil(sortedAndFilteredData.length / rowPerPage) > quickJumpSize
          }
          onChange={(page) => setPage(page)}
          quickJumperToStart
          hideOnSinglePage
        />
      </div>
    </div>
  );
};

export default ShutdownsTable;
